<template>
  <div :class="`layout-${layout}`" class="mds-form-control mds-form-item">
    <label :for="uid" v-if="label" :style="labelStyles">
      {{ label }}
      <span v-if="withAsterisk" class="asterisk">*</span>
    </label>

    <div class="inner">
      <div class="control-row" :class="ratioClass">
        <slot />

        <span class="unit" v-if="unit">{{ unit }}</span>
      </div>

      <div class="messages-row">
        <ul class="error" v-if="errors.length > 0">
          <li
            v-for="(error, index) in errors"
            :key="'error' + index"
          >
            {{ error.message }}
          </li>
        </ul>

        <ul class="warning" v-if="warnings.length > 0">
          <li
            v-for="(warning, index) in warnings"
            :key="'warning' + index"
          >
            {{ warning.message }}
          </li>
        </ul>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./VFormItem.scss" />

<script lang="ts">
type Ratio = '1/1' | '1/2' | '2/1';
const RATIOS: Ratio[] = ['1/1', '1/2', '2/1'];

import VBaseControl from "@/core/services/VBaseControl";
import Unit from "@/domains/Units/Unit";

/**
 * VFormItem — стилистическая обёртка, никакой логики валидации не содержит.
 * Аккумулирует сообщения вложенных контролов: при регистрации контрол гасит
 * свой блок сообщений, а VFormItem выводит его ошибки/предупреждения у себя.
 */
export default defineComponent({
  name: "VFormItem",

  // extends: VBaseControl,

  props: {
    /** Лейбл контрола */
    label: VBaseControl.props.label,
    layout: VBaseControl.props.layout,
    labelWidth: VBaseControl.props.labelWidth,

    /** Показывать звёздочку обязательности рядом с лейблом */
    withAsterisk: {
      type:    Boolean,
      default: false,
    },
    /**
     * Соотношение ширин контролов в строке. Числа — доли flex-grow.
     *   '1/1' — равные
     *   '1/2' — 1/3 на первый, 2/3 на второй
     *   '2/1' — 2/3 на первый, 1/3 на второй
     */
    ratio: {
      type:      String as PropType<Ratio>,
      default:   '2/1',
      validator: (value: string) => RATIOS.includes(value as Ratio),
    },
    /** Единица измерения справа от контролов в строке. Строка или объект Unit. */
    unit: {
      type:    [String, Object] as PropType<string | Unit>,
      default: null,
    },
  },

  emits: ["validate"],

  data() {
    return {
      uid:             VBaseControl.methods!.generateUid(),
      controls:        new Map(),
      validationState: new Map(),
    };
  },

  computed: {
    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },
    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },

    /**
     * Преобразует проп ratio в имя CSS-класса.
     * '1/2' → 'ratio-1-2'. В CSS работаем с дефисами вместо слешей,
     * чтобы не возиться с экранированием.
     */
    ratioClass(): string {
      return `ratio-${this.ratio.replaceAll('/', '-')}`;
    },

    /**
     * Плоский список ошибок вложенных контролов в виде
     * { message: string, fieldRef: ComponentInstance }.
     * Структура повторяет VForm.errors — один контрол может дать
     * несколько записей. Порядок — порядок регистрации.
     */
    errors() {
      const result = [];

      for (const [key, validation] of this.validationState) {
        if (validation.isValid === false) {
          const control = this.controls.get(key);
          for (const message of validation.errors) {
            result.push({ message, fieldRef: control });
          }
        }
      }

      return result;
    },

    /**
     * Плоский список предупреждений вложенных контролов. Как и в VForm,
     * формируется независимо от validation.isValid.
     */
    warnings() {
      const result = [];

      for (const [key, validation] of this.validationState) {
        if (validation.warnings && validation.warnings.length > 0) {
          const control = this.controls.get(key);
          for (const message of validation.warnings) {
            result.push({ message, fieldRef: control });
          }
        }
      }

      return result;
    },
  },

  methods: {
    /**
     * Вызывается контролом из VBaseControl.registerInParentFormItem().
     * Контрол гасит свой блок сообщений — выводим их вместо него.
     */
    registerControl(control) {
      if (control.isControl !== true) return;

      if (this.controls.has(control.uid)) return;

      this.controls.set(control.uid, control);
      control.hideErrors();

      this.updateValidationState(control);
    },

    unregisterControl(control) {
      if (!this.controls.has(control.uid)) return;

      this.controls.delete(control.uid);
      control.showErrors();

      this.updateValidationState(control);
    },

    /** Если нужно точечно что-то обновить, то передаёшь контрол который хочешь синкануть,
     * если нужно обновить всё состояние, то ничего не передаёшь */
    updateValidationState(control = null) {
      if (control) {
        if (this.controls.has(control.uid)) {
          this.validationState.set(control.uid, control.validation);
        } else {
          this.validationState.delete(control.uid);
        }
      } else {
        for (const [key, value] of this.controls) {
          this.validationState.set(key, value.validation);
        }
      }
    },
  },

});
</script>
